import { Fragment } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import type { DeserializedStorePageApp } from '@api';

import NextLink from '@shared/common/components/NextLink';
import { resolveAppUrl } from '@shared/utils/resolveAppUrl';

import StoreAppCard from '@store:web/components/common/AppCard/StoreAppCard';

interface AppBlockProps {
  title?: string;
  link?: string;
  apps: DeserializedStorePageApp[];
  isFull?: boolean;
  isHeader?: boolean;
  isMiniMobile?: boolean;
  className?: string;
  blockClassName?: string;
  size?: 'sm' | 'md';
}

const AppBlock = ({
  title,
  link = '/',
  size = 'md',
  apps,
  isFull = true,
  isMiniMobile = false,
  isHeader = true,
  className,
  blockClassName,
}: AppBlockProps) => {
  return (
    <div className={classNames('w-full', className)}>
      {isHeader && (
        <div className="lex-shrink-0 w-full flex justify-between items-start pb-4">
          <h2 className="t-h5 font-bold">{title}</h2>
          <NextLink
            href={link}
            className="text-base transition-colors text-primary-600 hover:text-primary-700 whitespace-nowrap"
            data-qa="apps-block-see-all-button"
          >
            See all ››
          </NextLink>
        </div>
      )}
      {!!apps.length && (
        <div
          className={classNames(
            {
              'grid lg:grid-cols-2 gap-4': !isFull,
              'flex justify-start md:grid grid-cols-1 gap-2': isFull,
            },
            blockClassName,
          )}
          data-qa="apps-grid"
        >
          {apps.map((app) => (
            <Fragment key={app.id}>
              <div
                data-qa="validated-app"
                className={classNames('shadow-main rounded-lg pb-8', {
                  'hidden md:block': isMiniMobile,
                  'p-2': size === 'sm',
                  'p-4': size === 'md',
                })}
              >
                <StoreAppCard app={app} />
              </div>
              {isMiniMobile && (
                <div className="block md:hidden">
                  <NextLink href={resolveAppUrl(app.attributes.appId, app.attributes.status)}>
                    <ImageProxy
                      alt={`${app.attributes.name} Icon`}
                      className="rounded-[0.625rem] w-[3.125rem] h-[3.125rem] flex-shrink-0"
                      src={app.attributes.icon}
                      width={50}
                      height={50}
                      data-qa="app-inside-block-image"
                    />
                  </NextLink>
                </div>
              )}
            </Fragment>
          ))}
        </div>
      )}
      {!apps.length && (
        <div className="w-full py-8">
          <h3 className="t-h4 text-center text-neutral-300">Empty!</h3>
        </div>
      )}
    </div>
  );
};

export default AppBlock;
